/* eslint-disable */
"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Moon, Sun } from "lucide-react";

type Theme = "dark" | "light";

const ThemeContext = createContext<{ theme: Theme; toggleTheme: () => void }>({
  theme: "dark",
  toggleTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

export function ThemeProvider({ children, defaultTheme }: { children: ReactNode; defaultTheme?: Theme }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (defaultTheme === "dark" || defaultTheme === "light") return defaultTheme;
    try {
      const saved = window.localStorage.getItem("payora-theme");
      if (saved === "light" || saved === "dark") return saved;
    } catch {
      /* private mode */
    }
    return "dark";
  });

  useEffect(() => {
    if (defaultTheme && defaultTheme !== theme) {
      setTheme(defaultTheme);
      try {
        window.localStorage.setItem("payora-theme", defaultTheme);
      } catch {
        /* private mode */
      }
    }
  }, [defaultTheme, theme]);

  useEffect(() => {
    try {
      window.localStorage.setItem("payora-theme", theme);
    } catch {
      /* private mode */
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem("payora-theme", next);
      } catch {
        /* private mode */
      }
      return next;
    });
  };

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

export function ThemeToggleButton({ compact = false }: { compact?: boolean }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      onClick={toggleTheme}
      data-testid="payora-theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={
        compact
          ? "inline-flex h-9 w-9 items-center justify-center rounded-xl bg-surface2 text-muted transition-all hover:text-accent"
          : "group flex w-full items-center gap-3 rounded-xl bg-surface2 px-3 py-2.5 text-left transition-all"
      }
    >
      <span className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface3 text-accent">
        <Sun
          className={`h-4 w-4 transition-all duration-300 ${
            isDark ? "scale-100 rotate-0 opacity-100" : "scale-50 -rotate-90 opacity-0 absolute"
          }`}
        />
        <Moon
          className={`h-4 w-4 transition-all duration-300 ${
            isDark ? "scale-50 rotate-90 opacity-0 absolute" : "scale-100 rotate-0 opacity-100"
          }`}
        />
      </span>
      {!compact && (
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-text">
            {isDark ? "Switch to light mode" : "Switch to dark mode"}
          </span>
          <span className="block text-[10px] font-medium uppercase tracking-wider text-faint">
            Currently {isDark ? "Midnight Blue" : "Daylight"}
          </span>
        </span>
      )}
    </button>
  );
}
